/* eslint-disable perfectionist/sort-imports */
import * as React from 'react';
import { Link, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import { Link as RouterLink } from 'react-router';
import AppBar, { type AppBarProps } from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined'; // Set up
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined'; // Open for tender
import { useParams, useRouter } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';
import { useUserStore, usePreferencesStore } from 'src/store';
import { HomeIcon } from './icons/Home';
import { useAuth, useLogout } from 'src/hooks/useAuth';

export const Header = ({ sx }: { sx?: AppBarProps['sx'] } = {}) => {
  const router = useRouter();
  const { inviteToken } = useParams();
  const { isAuthenticated } = useAuth();
  const user = useUserStore((s) => s.user);
  const setMode = usePreferencesStore((s) => s.setMode);
  const setCompletedSteps = usePreferencesStore((s) => s.setCompletedSteps);
  const { logout } = useLogout();

  const isSupplier = user?.role === 'SUPPLIER';

  // Contracts menu state
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleOpen = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  // Helpers for routes (adjust to your paths object)
  const gotoContractSetup = () => {
    setMode('contract');
    setCompletedSteps([]);
    router.push(paths.procurer.contract.root);
  };
  const gotoOpenForTender = () => router.push(paths.dashboard.contractsOpenForTender);

  return (
    <AppBar position="static" color="transparent" elevation={0} sx={sx}>
      <Toolbar
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}
      >
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'end' }}>
          <Link onClick={() => router.push('/')} color="inherit" sx={{ cursor: 'pointer' }}>
            <Typography variant="button">Home</Typography>
          </Link>

          <Link component={RouterLink} to={paths.about} color="inherit">
            <Typography variant="button">About</Typography>
          </Link>

          {isAuthenticated && (
            <>
              {/* Contracts dropdown */}
              <Box>
                <Box
                  onClick={handleOpen}
                  aria-controls={open ? 'contracts-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={open ? 'true' : undefined}
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
                >
                  <Typography variant="button">Contracts</Typography>
                  <KeyboardArrowDownIcon fontSize="small" />
                </Box>

                <Menu
                  id="contracts-menu"
                  anchorEl={anchorEl}
                  open={open}
                  onClose={handleClose}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  PaperProps={{
                    elevation: 2,
                    sx: { mt: 1, minWidth: 220, borderRadius: 2 },
                  }}
                >
                  {/* Optional: clicking the header itself goes to contracts dashboard */}
                  <MenuItem
                    onClick={() => {
                      handleClose();
                      router.push(paths.dashboard.contracts);
                    }}
                  >
                    <ListItemIcon>
                      <HomeIcon />
                    </ListItemIcon>
                    <ListItemText primary="Dashboard" />
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      handleClose();
                      gotoOpenForTender();
                    }}
                  >
                    <ListItemIcon>
                      <LocalOfferOutlinedIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Open for tender" />
                  </MenuItem>

                  {!isSupplier && (
                    <MenuItem
                      onClick={() => {
                        handleClose();
                        gotoContractSetup();
                      }}
                    >
                      <ListItemIcon>
                        <AssignmentOutlinedIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Set up contract" />
                    </MenuItem>
                  )}
                </Menu>
              </Box>

              {user?.role === 'PROCURER' && (
                <Link
                  onClick={() => router.push(`${paths.procurer.preferences.geography}?edit=true`)}
                  color="inherit"
                  sx={{ cursor: 'pointer' }}
                >
                  <Typography variant="button">Preferences</Typography>
                </Link>
              )}
            </>
          )}
        </Box>

        {isAuthenticated ? (
          <Button variant="outlined" sx={{ mr: 2, backgroundColor: '#ffffff' }} onClick={logout}>
            Sign out
          </Button>
        ) : (
          <Button
            variant="outlined"
            sx={{ mr: 2, backgroundColor: '#ffffff' }}
            onClick={() =>
              router.push(inviteToken ? `${paths.signIn}?inviteToken=${inviteToken}` : paths.signIn)
            }
          >
            Sign in
          </Button>
        )}
      </Toolbar>
    </AppBar>
  );
};
